/**
 * Split config and helper functions for the feature flag service.
 * See feature_flags.md for more details.
 */
import { useMemo } from 'react'

import { getTreatments, initSplitSdk, selectTreatmentValue } from '@splitsoftware/splitio-redux'

import { useAppSelector } from '@/client/hooks'
import store from '@/client/store'
import { useGetLoggedUserQuery } from '@/services/user'
import localFeatures from '@/utils/features/features-local.json'
import { ALL_FEATURES } from '@/utils/features/featuresList'

// Possible FF states
const ALLOWED_FEATURE_FLAG_STATES = ['on', 'off']

// Singleton
let splitIoInitialized = false

// Config for the SDK factory.
const splitSdkConfig = {
  core: {
    authorizationKey: process.env.NEXT_PUBLIC_SPLIT_API_KEY,
    key: 'key',
  },
  features: localFeatures, // Ignored when auth key is not localhost
}

const onSplitReadyCallback = () => {
  // Get all feature flags on store now that it is ready to properly evaluate.
  getFeaturesFlagsOnStore()
}

const splitStoreInit = () => {
  if (!splitIoInitialized) {
    splitIoInitialized = true

    // Init Split SDK
    store.dispatch(
      initSplitSdk({
        config: splitSdkConfig,
        onReady: onSplitReadyCallback,
      })
    )
  }
}

const getFeaturesFlagsOnStore = () => {
  const username = store.getState().general.username || 'key' // If not logged in use generic "key"
  // Dispatch all feature flags to store
  store.dispatch(
    getTreatments({
      splitNames: ALL_FEATURES,
      key: username,
    })
  )
}

const getFeatureFlag = (featureName) => {
  // Shortcut method for getting a featureName flag value
  return selectTreatmentValue(store.getState().splitio, featureName)
}

const useFeatureFlag = (featureFlagName: string) => {
  const { data: user } = useGetLoggedUserQuery()
  const splitIo = useAppSelector((state) => state.splitio)

  const featureFlag = useMemo(() => {
    const { isReady, treatments } = splitIo

    if (isReady && treatments[featureFlagName]) {
      const result = getFeatureFlag(featureFlagName)

      if (ALLOWED_FEATURE_FLAG_STATES.includes(result)) {
        return result === 'on'
      }
    }
  }, [featureFlagName, splitIo])

  splitStoreInit()

  return featureFlag && user?.data?.is_user_controller_feature_flag_on
}

export { splitSdkConfig, splitStoreInit, getFeaturesFlagsOnStore, getFeatureFlag, useFeatureFlag }
